/** @jsxImportSource theme-ui @jsxRuntime classic */
import useIntersection from '@hzdg/use-intersection';
import { useEffect } from 'react';

import type { IntersectionHandler } from '@hzdg/use-intersection';

/** A symbol that represents the default intersction observer root. */
const DEFAULT_ROOT_SENTINEL = {};

/**
 * A map of `Element` roots to `IntersectionHeap` objects,
 * where the entries in each heap are unique by target element,
 * and the top entry in each heap has the highest `intersectionRatio`.
 */
const intersectionHeaps = new WeakMap<
  Element | typeof DEFAULT_ROOT_SENTINEL,
  Map<Element, IntersectionObserverEntry>
>();

/**
 * `useMaxIntersection` is a React hook for components that care about
 * when their intersection with a root element has the
 * __highest intersection ratio__ compared to other `useMaxIntersection`
 * elements that are also intersecting the same root.
 *
 * Returns a callback ref that should be passed to the observed element.
 */
export function useMaxIntersection(
  /**
   * `handler` will receive an `IntersectionObserverEntry` object
   * each time the observed element's intersection with the root element
   * has the __highest intersection ratio__ when compared to other elements
   * intersecting the same root.
   */
  handler: IntersectionHandler,
  /**
   * Optional configuration for the intersection observer.
   */
  config?: IntersectionObserverInit,
): (node: HTMLElement | null) => void {
  const root = config?.root ?? DEFAULT_ROOT_SENTINEL;

  if (!intersectionHeaps.has(root)) {
    intersectionHeaps.set(root, new Map());
  }

  const heap = intersectionHeaps.get(root)!;

  useEffect(() => {
    return () => void heap.clear();
  }, [heap]);

  return useIntersection((entry) => {
    heap.set(entry.target, entry);

    if (entry.isIntersecting) {
      const maxEntry = [...heap.values()].sort(
        (a, b) => b.intersectionRatio - a.intersectionRatio,
      )[0];

      if (maxEntry.target === entry.target) {
        handler(entry);
      }
    }
  }, config);
}
